import { cloneDeep, difference, indexOf, isNumber, isString, maxBy, minBy, pull, pullAll } from 'lodash';
import { convertToPolygon, getRotatePosition } from '@/utils/collision-kit';
import { getGroupLayerProps } from '@/utils/layer-kit';
import { traverseTree, traverseTrees } from '@/utils/tree-kit';
import {
    IGroupExtension,
    IGroupLayerProps,
    LayerType,
    type IExtraInfo,
    type ILayerProps,
    type IPoint,
    type IStageResizeData,
    type IWatermarkLayerProps,
} from '@/version-types';
import { getBehavior } from '../behavior';
import { FlatLayerMap } from '../extensions/flat-layer-map';
import { copyOriginIndex, getGroupExtension, parseLayerId } from '../utils';
import { CoreBase } from './core-base';

/** 核心数据类 */
export class CoreData extends CoreBase {
    /** 存储扁平的层信息，用于图层信息查找 */
    public flatLayersMap: FlatLayerMap;

    /** 是否需要重新渲染所有舞台 */
    private isRerenderSort: boolean = false;
    /**
     * 多舞台，原始画布图层列表
     * [[画布1]=0, [画布2]=1]
     * 每次添加画布，只会增加这个列表，不会做删除
     * 对应的索引叫: originIndex
     */
    private originIdsList: string[][];
    /** 画布的额外信息，
     * 长度与 originIdsList 一致
     */
    private extraInfos: IExtraInfo[];
    /**
     * 需要渲染的舞台列表
     * [originIndex=1, originIndex=0]
     * 对应的索引叫: stageIndex
     * 当前激活的舞台索引: activeStageIndex
     */
    private renderStageList: number[];

    /** 存储自定义数据 */
    private customData: Record<string, unknown> = {};
    /** 自定义 api */
    private customApis: Map<string, unknown> = new Map();

    /** 删除图层协作操作类型的编辑版本 */
    private delLayerRealEditVersion: Record<string, Record<string, [number, string]>> = {};

    constructor(identity: string) {
        super(identity);
        this.originIdsList = [];
        this.renderStageList = [];
        this.extraInfos = [];
        this.flatLayersMap = new FlatLayerMap(identity, (layerId) => {
            this.events.emitLayerChange(layerId);
        });
    }

    destroy() {
        this.flatLayersMap.destroy();
        this.originIdsList.length = 0;
        this.extraInfos.length = 0;
        this.renderStageList.length = 0;
        this.isRerenderSort = false;
        this.delLayerRealEditVersion = {};
        super.destroy();
    }

    /** 获取快照 */
    getSnapshot() {
        const snapshot = {
            data: cloneDeep(this.flatLayersMap.data),
            extraInfos: cloneDeep(this.extraInfos),
            originIdsList: cloneDeep(this.originIdsList),
            renderStageList: cloneDeep(this.renderStageList),
        };
        // 剔除临时组信息
        snapshot.originIdsList.forEach((originIds) => {
            const { extraGroupIds } = this.resetExtraGroupInfo(originIds, (id) => snapshot.data[id]);
            const extraGroupIdsList = Array.from(extraGroupIds);
            pullAll(originIds, extraGroupIdsList);
            extraGroupIdsList.forEach((id) => {
                delete snapshot.data[id];
            });
        });
        // console.log('ot getScreenshot 3', screenshot);

        return snapshot;
    }

    /** 恢复快照 */
    restoreSnapshot(snapshot: ReturnType<typeof this.getSnapshot>) {
        this.flatLayersMap.data = snapshot.data;
        this.extraInfos.length = 0;
        this.extraInfos.push(...snapshot.extraInfos);
        this.originIdsList.length = 0;
        this.originIdsList.push(...snapshot.originIdsList);
        this.renderStageList.length = 0;
        this.renderStageList.push(...snapshot.renderStageList);
        // 刷新所有舞台
        this.incrVersion();
        this.setRerenderSort();
        this.rerender();
    }

    /** 舞台额外信息 *************************************************/

    /**
     * 添加舞台额外信息
     *
     * @param extraInfo - 额外信息
     */
    addExtraInfos(extraInfos: IExtraInfo[], originIndexs?: number[]) {
        if (originIndexs && originIndexs.length) {
            for (let i = 0; i < originIndexs.length; i++) {
                const originIndex = originIndexs[i];
                this.extraInfos[originIndex] = extraInfos[i];
            }
        } else {
            this.extraInfos.push(...extraInfos);
        }
    }

    /**
     * 设置舞台额外信息
     *
     * @param stageIndex - 舞台索引
     * @param extraInfo - 额外信息
     */
    setExtraInfo(stageIndex: number, extraInfo: IExtraInfo) {
        const originIndex = this.convertStageToOrigin(stageIndex);
        this.extraInfos[originIndex] = extraInfo;
    }

    /**
     * 设置图层额外信息
     *
     * @param layerId - 图层id
     * @param extraInfo - 额外信息
     */
    setExtraInfoByLayerId(layerId: string, extraInfo: IExtraInfo) {
        const originIndex = this.tryGetLayerOriginIndex(layerId);
        if (isNumber(originIndex)) {
            this.extraInfos[originIndex] = extraInfo;
        }
    }

    /**
     * 获取舞台额外信息
     *
     * @param stageIndex - 舞台索引
     */
    getExtraInfo(stageIndex: number) {
        const originIndex = this.convertStageToOrigin(stageIndex);
        return cloneDeep(this.extraInfos[originIndex]) || {};
    }

    /** 获取舞台额外信息列表 */
    getExtraInfoList() {
        return this.renderStageList.map((originIndex) => {
            return this.getExtraInfo(this.convertOriginToStage(originIndex));
        });
    }

    // /** 更新舞台额外信息尺寸 */
    // updateExtraInfoSize(stageIndex: number) {
    //     const size = this.getSizeByIndex(stageIndex);
    //     this.setExtraInfo(stageIndex, { ...this.getExtraInfo(stageIndex), ...size });
    // }

    /** 数据操作 *************************************************/

    /** 获取舞台渲染id列表，不包含临时组 */
    getRenderIdsWithoutExtraGroup(originIndex: number) {
        return this.getRenderIds(originIndex).filter((id) => !this.checkIsExtraGroup(this.getLayer(id)));
    }

    /** 获取舞台渲染id列表，只包含临时组 */
    pickRenderIdsExtraGroup(originIndex: number) {
        return this.getRenderIds(originIndex).filter((id) => this.checkIsExtraGroup(this.getLayer(id)));
    }

    /** 获取扁平的图层信息 */
    getFlatLayersMap() {
        return this.flatLayersMap;
    }

    /** 获取舞台原始索引对应的图层id */
    getRenderIds(originIndex: number) {
        return this.tryGetOriginIdsAndIndex(originIndex).originIds;
    }

    /** 获取舞台渲染索引 */
    getRenderStage() {
        return this.renderStageList;
    }

    /** 检查舞台是否渲染 */
    checkStageIsRender(originIndex: number) {
        return this.renderStageList.indexOf(originIndex) !== -1;
    }

    /** 检查是否是旧的舞台 */
    checkIsOldOriginIndex(originIndex: number) {
        return this.originIdsList[originIndex]?.length > 0;
    }

    /** 设置舞台渲染索引 */
    setRenderStage(renderStageList: number[]) {
        this.renderStageList.length = 0;
        this.renderStageList.push(...renderStageList);
    }

    /** 获取舞台渲染数量 */
    getRenderStageLength() {
        return this.renderStageList.length;
    }

    /** 获取舞台原始id列表 */
    getOriginIdsList() {
        return this.originIdsList;
    }

    /** 设置需要重新渲染的舞台原始索引 */
    setRerender(originIndex: number) {
        this.flatLayersMap.rerenderOriginIndexes.add(originIndex);
    }

    /** 设置需要重新渲染所有舞台 */
    setRerenderSort() {
        this.isRerenderSort = true;
    }

    /** 设置需要重新渲染的图层 */
    setRenderLayerIds(layerIds: string[]) {
        this.flatLayersMap.setRenderLayerIds(layerIds);
    }

    /** 工具函数 *************************************************/

    /** 获取图层重叠列表 */
    getLayerOverLapList(layerId: string, ignoreTypes: LayerType[] = []) {
        const layer = this.getLayer(layerId, true);
        const originIndex = this.tryGetLayerOriginIndex(layerId) || 0;
        const layerPolygon = convertToPolygon(layer);
        const renderIds = this.getRenderIds(originIndex);
        const overLapList: ILayerProps[] = [];

        // 只插入一级组，其子组元素全部插入一级组
        const setChildren = (layer: ILayerProps, targetArr?: ILayerProps[]) => {
            const children: ILayerProps[] = targetArr ?? [];
            (layer.children || []).forEach((item) => {
                const res = this.getLayer(item.id, true);
                if (canInsert(res)) {
                    if (res.layerType === LayerType.GROUP_LAYER) {
                        setChildren(res, children);
                    } else {
                        children.unshift(res);
                    }
                }
            });
            if (!targetArr) layer.children = children;
        };

        // 判断是否与图层相交可以插入
        const canInsert = (curLayer: ILayerProps) => {
            if (!curLayer) return;
            const curLayerPolygon = convertToPolygon(curLayer);
            if (!this.checkIsNeedHidden(curLayer) && layerPolygon.polygon.collidesWith(curLayerPolygon.polygon)) {
                return true;
            }
            return false;
        };

        for (let i = 0; i < renderIds.length; i++) {
            const id = renderIds[i];
            const item = this.getLayer(id, true);
            if (!item || item.parentId || indexOf(ignoreTypes, item.layerType) !== -1) continue;
            if (this.checkIsNeedHidden(item)) continue;
            // 全屏水印没有重叠选择的必要, 改到图层behavior中处理
            // if (item.layerType === LayerType.WATERMARK && (item.data as IWatermarkLayerProps).isFullScreen) continue;
            const disableCollision = getBehavior(this.identity, item)?.checkDisableCollision?.(item);
            if (disableCollision) continue;
            const itemPolygon = convertToPolygon(item);
            if (layerPolygon.polygon.collidesWith(itemPolygon.polygon)) {
                setChildren(item);
                overLapList.push(item);
            }
        }
        return overLapList;
    }

    /**
     * 获取图层排序列表
     * @param layerId - 图层ID
     * @param ignoreTypes - 忽略的图层类型
     * @returns 排序列表
     */
    getLayerSortData(layerId: string, ignoreTypes: LayerType[] = []) {
        // 获取跟图层交叉的图层
        const layer = this.getLayer(layerId);
        const childIds = this.checkIsExtraGroup(layer) ? layer.children?.map((item) => item.id) : [layer.id];
        const layerPolygon = convertToPolygon(layer);
        const originIndex = this.tryGetLayerOriginIndex(layerId) || 0;
        const renderIds = this.getRenderIds(originIndex);
        const sortList: { id: string; index: number }[] = [];
        const childList: { id: string; index: number }[] = [];
        for (let i = 0; i < renderIds.length; i++) {
            const id = renderIds[i];
            const item = this.getLayer(id);
            let parentIsFormalGroup = false;
            if (item?.parentId) {
                const parent = this.getLayer(item.parentId);
                parentIsFormalGroup = this.checkIsFormalGroup(parent);
            }
            if (!item || (item.parentId && parentIsFormalGroup) || indexOf(ignoreTypes, item.layerType) !== -1)
                continue;
            if (this.checkIsExtraGroup(item) || this.checkIsNeedHidden(item)) continue;
            if (getBehavior(this.identity, item)?.checkDisableCollision?.(item)) continue;
            const itemPolygon = convertToPolygon(item);
            if (layerPolygon.polygon.collidesWith(itemPolygon.polygon)) {
                const index = indexOf(renderIds, item.id);
                const data = {
                    id: item.id,
                    index,
                };
                if (childIds.indexOf(item.id) !== -1) {
                    childList.push(data);
                } else {
                    sortList.push(data);
                }
            }
        }
        const topItem = childList[childList.length - 1];
        const bottomItem = childList[0];
        // 可下移几个
        let downCount = 0;
        let upCount = 0;
        for (let i = 0; i < sortList.length; i++) {
            const item = sortList[i];
            if (item.index < topItem.index) {
                downCount++;
            }
            if (item.index > bottomItem.index) {
                upCount++;
            }
        }
        const disableDown = downCount === 0;
        const disableUp = upCount === 0;
        const disableSort = disableDown && disableUp;
        return {
            data: {
                /** 原始索引 */
                originIndex,
                /** 排序列表 */
                sortList,
                /** 子列表 */
                childList,
            },
            cursor: {
                /** 当前索引 */
                current: 0,
                /** 可下移 */
                downCount: -downCount,
                /** 可上移 */
                upCount,
            },
            /** 操作 */
            operate: {
                /** 下移 */
                disableDown,
                /** 上移 */
                disableUp,
                /** 禁用排序 */
                disableSort,
            },
        };
    }

    /** 检查图层是否需要隐藏 */
    checkIsHidden(layer: ILayerProps) {
        return layer.hidden || layer.__isHidden;
    }

    /**
     * 获取碰撞图层
     *
     * @param originIndex - 舞台原始索引
     * @param ignoreLock - 可选；是否忽略锁定图层；默认值为 `true`
     */
    getCollisionLayers(originIndex: number, ignoreLock: boolean = true, useTryPick: boolean = false) {
        const layers = this.getLayers(originIndex);
        const res: ILayerProps[] = [];
        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            const skip = (ignoreLock && layer.lock) || this.checkIsNeedHidden(layer);
            if (!skip) {
                if (!this.checkIsGroupExtension(layer) && this.checkIsGroup(layer)) {
                    if (this.checkIsExtraGroup(layer)) {
                        layer.children?.forEach((item) => {
                            res.push(item);
                        });
                    } else {
                        res.push(layer);
                    }
                } else {
                    if (useTryPick && getBehavior(this.identity, layer)?.checkTryPick) {
                        if (getBehavior(this.identity, layer)?.checkTryPick?.(layer)) {
                            res.push(layer);
                        }
                    } else {
                        const disableInGroup = getBehavior(this.identity, layer)?.checkDisableInGroup?.(layer);
                        if (!disableInGroup) {
                            res.push(layer);
                        }
                    }
                }
            }
        }
        return res;
    }

    /** 获取主图层 */
    getMainLayer(originIndex: number) {
        const originIds = this.getRenderIds(originIndex);
        for (let i = 1; i < Math.min(originIds.length, 2); i++) {
            const layer = this.flatLayersMap.get(originIds[i]);
            if (layer) {
                if (getBehavior(this.identity, layer)?.isMainLayer?.()) {
                    return layer as ILayerProps;
                }
            }
        }
        return null;
    }

    /** 获取背景图层 */
    getBackgroundLayer(originIndex: number) {
        const originIds = this.getRenderIds(originIndex);
        for (let i = 0; i < Math.min(originIds.length, 1); i++) {
            const layer = this.flatLayersMap.get(originIds[i]);
            if (layer) {
                if (getBehavior(this.identity, layer)?.isBackgroundLayer?.()) {
                    return layer as ILayerProps;
                }
            }
        }
        return null;
    }

    /** 获取画布距离舞台顶部的偏移量 */
    getStageTop(stageIndex: number) {
        let top = 0;
        for (let i = 0; i < stageIndex; i++) {
            const originIndex = this.convertStageToOrigin(i);
            top += this.getBackgroundLayer(originIndex)?.height || 0;
        }

        return top;
    }

    /** 检查图层是否是脏数据 */
    checkLayerIsDirty({
        originLayer,
        latestIsExtraGroup = false,
        latestIsFormalGroup = false,
    }: {
        /** 原始图层 */
        originLayer: ILayerProps;
        /** 最新图层是否是临时组 */
        latestIsExtraGroup?: boolean;
        /** 最新图层是否是正式组 */
        latestIsFormalGroup?: boolean;
    }) {
        let isDirty = false;
        const checkLayersParentIsDirty = (layers: ILayerProps[]) => {
            let isDirty = false;
            traverseTrees(layers, (node) => {
                const oldParentId = node.parentId || '';
                const layer = this.getLayer(node.id);
                const newParentId = layer?.parentId || '';
                if (!layer || oldParentId !== newParentId) {
                    isDirty = true;
                }
            });
            return isDirty;
        };
        const latestLayer = this.getLayer(originLayer.id);
        const waitCheckLayers: ILayerProps[] = [];

        // 检查最新数据是否为临时组
        if (latestIsExtraGroup) {
            if (latestLayer) {
                if (!this.checkIsExtraGroup(latestLayer)) {
                    isDirty = true;
                } else {
                    waitCheckLayers.push(originLayer);
                }
            } else {
                originLayer.children?.forEach((item) => {
                    const layer = this.getLayer(item.id);
                    if (!layer) {
                        isDirty = true;
                    }
                    if (!isDirty) {
                        const parentId = layer?.parentId || '';
                        if (parentId && !this.checkIsExtraGroup(this.getLayer(parentId))) {
                            isDirty = true;
                        }
                    }
                    if (!isDirty && item.children) {
                        waitCheckLayers.push(...item.children);
                    }
                });
            }
            if (!isDirty) {
                isDirty = checkLayersParentIsDirty(waitCheckLayers);
            }
            return isDirty;
        }

        // 检查最新数据是否为正式组
        if (!latestLayer || (latestIsFormalGroup && !this.checkIsFormalGroup(latestLayer))) {
            isDirty = true;
        } else {
            // 检查图层是否一致
            const oldIds: string[] = [];
            const newIds: string[] = [];
            traverseTree(originLayer, (node) => {
                oldIds.push(node.id);
            });
            traverseTree(latestLayer, (node) => {
                newIds.push(node.id);
            });
            const diff = difference(oldIds, newIds);
            if (diff.length) {
                isDirty = true;
            }
            waitCheckLayers.push(originLayer);
        }

        if (!isDirty) {
            isDirty = checkLayersParentIsDirty(waitCheckLayers);
        }

        return isDirty;
    }

    /** 检查图层是否相等 */
    checkLayerEqual(originLayer: ILayerProps, latestLayer: ILayerProps) {
        if (!originLayer || !latestLayer) return false;
        const originParentId = originLayer.parentId || '';
        const latestParentId = latestLayer.parentId || '';
        if (originParentId !== latestParentId) return false;
        const originIds: string[] = [];
        const latestIds: string[] = [];
        traverseTree(originLayer, (node) => {
            originIds.push(node.id);
        });
        traverseTree(latestLayer, (node) => {
            latestIds.push(node.id);
        });
        return difference(originIds, latestIds).length === 0;
    }

    /** 检查图层是否是临时组 */
    checkIsExtraGroup(layer: ILayerProps) {
        return this.flatLayersMap.checkIsExtraGroup(layer);
    }

    /** 检查图层是否是正式组 */
    checkIsFormalGroup(layer: ILayerProps) {
        return this.flatLayersMap.checkIsFormalGroup(layer);
    }

    /**
     * 检查图层是否可以拆分
     *
     * @param layer - 图层属性，会递归检查父级
     */
    checkPossibleSplit(layer: ILayerProps) {
        let possibleUnpack = false;
        let stop = false;

        const check = (layer: ILayerProps) => {
            if (layer) {
                const extension = getGroupExtension<IGroupExtension>(layer);
                if (extension) {
                    possibleUnpack = !!getBehavior(this.identity, layer)?.possibleUnpack?.(layer);
                    stop = true;
                }
            }
            if (!stop && !possibleUnpack && layer?.parentId) {
                check(this.getLayer(layer.parentId));
            }
        };

        check(layer);

        if (stop) {
            return possibleUnpack;
        }

        return true;
    }

    /**
     * 检查是否可以剪切
     *
     * @param layer - 图层属性
     */
    checkPossibleCut(layer: ILayerProps) {
        // 如果剪切的是拓展组的组内元素，并且组内元素不能拆分，则不进行剪切
        if (this.checkWrapIsGroupExtension(layer)) {
            if (layer?.parentId && !this.checkPossibleSplit(layer)) {
                return false;
            }
        }
        return true;
    }

    /**
     * 检查是否可以删除
     *
     * @param layer - 图层属性
     */
    checkPossibleDelete(layer: ILayerProps) {
        return this.checkPossibleCut(layer);
    }

    /** 检查图层是否禁用操作 */
    checkDisableChildBaseOperate(layer?: ILayerProps | null) {
        if (!layer) return false;

        let disableOperate = false;
        let stop = false;

        const check = (layer: ILayerProps) => {
            if (layer) {
                const checkDisableChildBaseOperate = getBehavior(this.identity, layer)?.checkDisableChildBaseOperate;
                if (checkDisableChildBaseOperate) {
                    disableOperate = checkDisableChildBaseOperate(layer);
                    stop = true;
                }
            }
            if (!stop && !disableOperate && layer?.parentId) {
                check(this.getLayer(layer.parentId));
            }
        };

        check(layer);

        if (stop) {
            return disableOperate;
        }

        return false;
    }

    /**
     * 检查是否可以解组
     *
     * @param layer - 图层属性
     */
    checkBrokenGroup(layer: ILayerProps | null) {
        if (!layer) return false;
        // 判断拓展图层是否可以拆分
        const extension = getGroupExtension<IGroupExtension>(layer);
        if (extension) {
            return !!getBehavior(this.identity, layer)?.possibleUnpack?.(layer);
        }
        // 判断是否是vip花字图层，如果是则不能拆分
        return !this.checkIsTextMaterialVip(layer);
    }

    /** 检查图层是否是vip花字图层 */
    checkIsTextMaterialVip(layer?: ILayerProps | null) {
        if (!layer) return false;
        return (layer.data as IGroupLayerProps)?.isTextMaterial && (layer.data as IGroupLayerProps)?.isVip;
    }

    /**
     * 检查图层是否是拓展图层
     *
     * @param layer - 图层属性
     */
    checkIsGroupExtension(layer?: ILayerProps | null) {
        if (!layer) return false;
        const extension = getGroupExtension<IGroupExtension>(layer);
        return !!extension;
    }

    /** 检查外壳图层是否是拓展图层 */
    checkWrapIsGroupExtension(layer?: ILayerProps | null) {
        if (!layer) return false;
        if (this.checkIsGroupExtension(layer)) return true;
        if (layer.parentId) {
            const parent = this.getLayer(layer.parentId);
            if (parent) {
                return this.checkWrapIsGroupExtension(parent);
            }
        }
        return false;
    }

    /** 检查图层是否是组 */
    checkIsGroup(layer: ILayerProps) {
        return this.flatLayersMap.checkIsGroup(layer);
    }

    /** 检查图层是否是背景图层 */
    checkIsBackground(layer: ILayerProps) {
        return this.flatLayersMap.checkIsBackground(layer);
    }

    /** 检查图层是否是主图层 */
    checkIsMain(layer: ILayerProps) {
        return this.flatLayersMap.checkIsMain(layer);
    }

    /** 检查图层是否需要隐藏 */
    checkIsNeedHidden(layerId: string | ILayerProps) {
        const wrapLayer = isString(layerId) ? this.flatLayersMap.get(layerId) : layerId;
        if (!wrapLayer) return true;
        let needHidden = !!wrapLayer.hidden || !!wrapLayer.__isHidden;

        const updateWrapLayer = (id: string, currentLayer: ILayerProps) => {
            const layer = this.flatLayersMap.get(id);
            if (!layer) {
                needHidden = !!currentLayer.hidden || !!currentLayer.__isHidden;
                return;
            }
            if (layer.hidden || layer.__isHidden) {
                needHidden = true;
                return;
            }
            if (layer.parentId) {
                updateWrapLayer(layer.parentId, layer);
            } else {
                needHidden = !!layer.hidden || !!layer.__isHidden;
            }
        };

        if (wrapLayer.parentId) {
            updateWrapLayer(wrapLayer.parentId, wrapLayer);
        }

        return needHidden;
    }

    /** 检查图层是否需要锁定 */
    checkIsNeedLock(layerId: string) {
        const wrapLayer = this.flatLayersMap.get(layerId);
        if (!wrapLayer) return true;
        let needLock = !!wrapLayer.lock;

        const updateWrapLayer = (id: string, currentLayer: ILayerProps) => {
            const layer = this.flatLayersMap.get(id);
            if (!layer) {
                needLock = !!currentLayer.lock;
                return;
            }
            if (layer.lock) {
                needLock = true;
                return;
            }
            if (layer.parentId) {
                updateWrapLayer(layer.parentId, layer);
            } else {
                needLock = !!layer.lock;
            }
        };

        if (wrapLayer.parentId) {
            updateWrapLayer(wrapLayer.parentId, wrapLayer);
        }

        return needLock;
    }

    /** 检查图层是否需要渲染 */
    checkIsNeedRender(layerId: string) {
        const originIndex = this.tryGetLayerOriginIndex(layerId);
        if (isNumber(originIndex)) {
            return this.originIdsList[originIndex]?.indexOf(layerId) !== -1;
        }
        return false;
    }

    /** 检查组图层是否可以调整宽高 */
    checkGroupLayerCanResize(children: ILayerProps[]) {
        let hasHorizontal = true,
            hasVertical = true;
        const traverse = (items: ILayerProps[]) => {
            for (const child of items) {
                const { horizontal, vertical } = getBehavior(this.identity, child)?.whenGroupCanResize?.(child) ?? {
                    horizontal: false,
                    vertical: false,
                };
                hasHorizontal = hasHorizontal && horizontal;
                hasVertical = hasVertical && vertical;
                if (child.layerType === LayerType.GROUP_LAYER && child.children) {
                    traverse(child.children);
                }
            }
        };
        traverse(children);
        return { hasHorizontal, hasVertical };
    }

    /** 检查图层是否可以调整宽高 */
    checkCanResizeOperate = (layer: ILayerProps) => {
        let resizeState = {
            top: true,
            bottom: true,
            left: true,
            right: true,
        };
        if (!layer) return resizeState;
        const params = getBehavior(this.identity, layer)?.onCheckCanResize?.(layer);
        if (params) {
            resizeState = {
                top: params.vertical,
                bottom: params.vertical,
                left: params.horizontal,
                right: params.horizontal,
            };
        }

        // 组的逻辑
        const children = layer?.children;
        if (children && children.length) {
            const { hasHorizontal, hasVertical } = this.checkGroupLayerCanResize(children);
            resizeState = {
                top: hasVertical,
                bottom: hasVertical,
                left: hasHorizontal,
                right: hasHorizontal,
            };
        }
        return resizeState;
    };

    /** 渲染索引转原始索引 */
    convertStageToOrigin(stageIndex: number) {
        return this.renderStageList[stageIndex] || 0;
    }

    /** 原始索引转渲染索引 */
    convertOriginToStage(originIndex: number) {
        const index = this.renderStageList.indexOf(originIndex);
        return index === -1 ? 0 : index;
    }

    /** 尝试获取图层原始索引 */
    tryGetLayerOriginIndex(layerId: string) {
        return parseLayerId(layerId).originIndex;
    }

    /** 尝试移除临时组 */
    tryRemoveGroup(layer: ILayerProps | null, autoRerender = false) {
        if (!layer || !this.checkIsExtraGroup(layer)) return;
        const originIndex = this.tryGetLayerOriginIndex(layer.id);
        if (!isNumber(originIndex)) return;
        const renderIds = this.getRenderIds(originIndex);
        if (renderIds.indexOf(layer.id) === -1) return;
        layer.children?.forEach((item) => {
            const layer = this.getLayer(item.id);
            if (layer) {
                layer.parentId = undefined;
            }
        });
        pull(renderIds, layer.id);
        this.setRerender(originIndex);
        this.flatLayersMap.delete(layer.id);
        if (autoRerender) {
            this.rerender();
        }
        return true;
    }

    /** 尝试创建临时组 */
    tryCreateGroup(originIndex: number, groupId: string, childIdsSet: Set<string>) {
        const positions: IPoint[] = [];
        const children: ILayerProps[] = [];
        let newGroupId = '';
        let i = 0;
        const renderIds = this.getRenderIds(originIndex);
        // let maxChild
        renderIds.forEach((id) => {
            if (childIdsSet.has(id)) {
                if (i === 0) {
                    newGroupId = copyOriginIndex(groupId, id);
                }
                i++;
                const layer = this.getLayer(id);
                if (!layer) return;
                const { left, top, width, height, rotate } = layer;
                const position = getRotatePosition({ left, top, width, height, rotate });
                positions.push(...position);
                layer.parentId = newGroupId;
                children.push(layer);
            }
        });
        if (!positions.length || !newGroupId) {
            return null;
        }
        const minX = minBy(positions, (pos) => pos.x) as IPoint;
        const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
        const minY = minBy(positions, (pos) => pos.y) as IPoint;
        const maxY = maxBy(positions, (pos) => pos.y) as IPoint;
        const baseInfo = {
            left: minX.x,
            top: minY.y,
            width: maxX.x - minX.x,
            height: maxY.y - minY.y,
            rotate: 0,
            children,
        };
        return getGroupLayerProps({
            id: newGroupId,
            ...baseInfo,
            data: {
                isExtraGroup: true,
            },
        });
    }

    /** 获取舞台原始索引信息 */
    tryGetOriginIdsAndIndex(originIndex: number, forceAdd = false) {
        let originIds: string[] = this.originIdsList[originIndex];
        if (!originIds) {
            originIds = [];
            if (forceAdd) {
                this.originIdsList.push(originIds);
                originIndex = this.originIdsList.length - 1;
            }
        }
        return {
            originIds,
            originIndex,
        };
    }

    /** 获取舞台的基础数据 */
    tryGetStageTransform(originIndexOrLayerId: number | string, scale: number, isLongImageMode: boolean) {
        let originIndex = 0;
        if (typeof originIndexOrLayerId === 'string') {
            const res = this.tryGetLayerOriginIndex(originIndexOrLayerId);
            if (isNumber(res)) {
                originIndex = res;
            }
        } else {
            originIndex = originIndexOrLayerId;
        }
        const { width, height } = this.getSize(originIndex);
        let top = 0;
        if (isLongImageMode) {
            const index = this.renderStageList.indexOf(originIndex);
            if (index !== -1) {
                const sizeList = this.getSizeList();
                for (let i = 0; i < sizeList.length; i++) {
                    if (i < index) {
                        top += sizeList[i].height;
                    }
                    if (i === index) {
                        break;
                    }
                }
            }
        }
        return {
            width: width * scale,
            height: height * scale,
            /** 长图模式下，舞台的偏移量 */
            offsetTop: top * scale,
            originIndex,
        };
    }

    /** 尺寸 *************************************************/

    /** 获取舞台尺寸列表 */
    getSizeList(scale = 1) {
        return this.renderStageList.map((item) => {
            const { width, height } = this.getBackgroundLayer(item) || { width: 0, height: 0 };
            return { width: width * scale, height: height * scale };
        });
    }

    /** 获取指定渲染的舞台尺寸 */
    getSizeByIndex(stageIndex: number) {
        return this.getSize(this.convertStageToOrigin(stageIndex));
    }

    /** 获取舞台尺寸 */
    getSize(originIndex: number) {
        const { width, height } = this.getBackgroundLayer(originIndex) || {
            width: 0,
            height: 0,
        };
        return { width: ~~width, height: ~~height };
    }

    /** 获取长图尺寸 */
    getLongSize() {
        const sizeList = this.getSizeList();
        return {
            width: sizeList[0]?.width ?? 0,
            height: sizeList.reduce((sum, size) => sum + size.height, 0),
        };
    }

    /** 图层 *************************************************/

    /**
     * 获取外壳图层
     *
     * @param id - 图层 ID
     * @param extraGroupIsWrap - 可选；临时组是否可以作为外壳；默认值为 `false`
     */
    getWrapLayer(layerId: string, extraGroupIsWrap = false) {
        return this.flatLayersMap.getWrapLayer(layerId, extraGroupIsWrap);
    }

    /** 获取拷贝的图层，并执行 onBeforeGetLayerWithClone 行为 */
    private getLayerWithClone(layer: ILayerProps) {
        if (!layer) {
            return layer;
        }
        traverseTree(layer, (item) => {
            getBehavior(this.identity, item)?.onBeforeGetLayerWithClone?.(item);
        });
        return layer;
    }

    /** 获取图层 */
    getLayer(layerId: string, withClone = false) {
        const layer = this.flatLayersMap.get(layerId);
        if (withClone && layer) {
            return this.getLayerWithClone(cloneDeep(layer));
        }
        return layer;
    }

    /** 根据渲染索引, 获取舞台图层 */
    getLayersByIndex(stageIndex: number) {
        const originIndex = this.convertStageToOrigin(stageIndex);
        return this.getLayers(originIndex);
    }

    private parseEditType(editType: string) {
        const types = editType.split(':');
        return {
            key: types[1] || types[0],
            value: types[1] ? editType : '',
        };
    }

    /** 设置图层协作操作类型的编辑版本 */
    setRealEditTypeVersion(layerId: string, editType: string, serverTime: number) {
        const type = this.parseEditType(editType);
        const layer = this.flatLayersMap.get(layerId);
        if (layer) {
            const renderVersion = layer?.__renderRealEditVersion || {};
            const version = renderVersion[type.key] || [0, type.value];
            if (version[0] < serverTime) {
                renderVersion[type.key] = [serverTime, type.value];
                layer.__renderRealEditVersion = renderVersion;
                // console.log('ot 写入renderVersion 1', layerId, cloneDeep(renderVersion));
                return renderVersion;
            }
        } else {
            this.delLayerRealEditVersion[layerId] = this.delLayerRealEditVersion[layerId] || {};
            this.delLayerRealEditVersion[layerId][type.key] = [serverTime, type.value];
            // console.log('ot 写入renderVersion 2', layerId, cloneDeep(this.delLayerRealEditVersion[layerId][type.key]));
        }
        return;
    }

    /** 获取图层协作操作类型的编辑版本 */
    getRealEditTypeVersion(layerId: string, editType: string): [number, string] {
        const type = this.parseEditType(editType);
        const layer = this.flatLayersMap.get(layerId);
        if (layer) {
            const renderVersion = layer.__renderRealEditVersion?.[type.key] || [0, type.value];
            // console.log('ot 获取renderVersion 1', layerId, cloneDeep(renderVersion));
            return renderVersion;
        }
        // console.log('ot 获取renderVersion 2', layerId, cloneDeep([0, type.value]));
        return this.delLayerRealEditVersion[layerId]?.[type.key] || [0, type.value];
    }

    /** 重置临时组信息 */
    private resetExtraGroupInfo(ids: string[], getLayer: (id: string) => ILayerProps) {
        const layers: ILayerProps[] = [];
        const extraGroupIds: Set<string> = new Set();
        const groupIds: Set<string> = new Set();
        ids.forEach((id: string) => {
            const layer = this.getLayerWithClone(getLayer(id));
            if (!layer) return;
            if (layer.parentId) {
                if (!extraGroupIds.has(layer.parentId) && !groupIds.has(layer.parentId)) {
                    const parent = this.flatLayersMap.get(layer.parentId);
                    if (parent) {
                        if (this.checkIsExtraGroup(parent)) {
                            extraGroupIds.add(parent.id);
                        } else {
                            groupIds.add(parent.id);
                        }
                    }
                }
                if (extraGroupIds.has(layer.parentId)) {
                    layer.parentId = '';
                }
            }
            if (!this.checkIsExtraGroup(layer)) {
                layers.push(layer);
            }
        });
        return {
            layers,
            extraGroupIds,
            groupIds,
        };
    }

    /** 根据原始索引, 获取舞台图层 */
    getLayers(originIndexOrLayerIds: number | string[]) {
        let originIds: string[] = [];
        if (isNumber(originIndexOrLayerIds)) {
            originIds = this.tryGetOriginIdsAndIndex(originIndexOrLayerIds).originIds;
        } else {
            originIds = originIndexOrLayerIds;
        }
        return this.resetExtraGroupInfo(originIds, (id) => cloneDeep(this.flatLayersMap.get(id))).layers;
    }

    /** 获取所有舞台数据 */
    getStages() {
        return this.renderStageList.map((originIndex) => this.getLayers(originIndex));
    }

    /** 刷新舞台 */
    rerender() {
        const fromSetIndexes = Array.from(this.flatLayersMap.rerenderIdsMap.keys());
        const originIndexes = Array.from(this.flatLayersMap.rerenderOriginIndexes);
        const diffIndexes = difference(fromSetIndexes, originIndexes);
        const layerIds = diffIndexes
            .map((index) => Array.from(this.flatLayersMap.rerenderIdsMap.get(index) || []))
            .flat();
        const isRerenderSort = this.isRerenderSort;
        const resizeOriginIndexes = Array.from(this.flatLayersMap.resizeOriginIndexes);

        this.isRerenderSort = false;
        this.flatLayersMap.rerenderIdsMap.clear();
        this.flatLayersMap.rerenderOriginIndexes.clear();
        this.flatLayersMap.resizeOriginIndexes.clear();

        // console.log(
        //     'rerender',
        //     {
        //         isRerenderSort,
        //         layerIds,
        //         originIndexes,
        //         resizeOriginIndexes,
        //     },
        //     this.renderStageList,
        // );

        if (isRerenderSort) {
            // 刷新所有舞台
            this.events.emitRerenderAll([...this.renderStageList]);
            resizeOriginIndexes.length = 0;
            resizeOriginIndexes.push(...this.renderStageList);
        } else {
            // 刷新指定图层
            for (const layerId of layerIds) {
                this.events.emitRerenderLayer(layerId);
            }
            // 刷新指定舞台
            for (const originIndex of originIndexes) {
                this.events.emitRerenderStage(originIndex);
                resizeOriginIndexes.push(originIndex);
            }
        }

        const finalResizeIndexs = Array.from(new Set(resizeOriginIndexes));
        // 舞台尺寸变化
        if (finalResizeIndexs.length) {
            const changeData: IStageResizeData[] = [];
            for (const originIndex of finalResizeIndexs) {
                changeData.push({
                    originIndex,
                    stageIndex: this.convertOriginToStage(originIndex),
                    size: this.getSize(originIndex),
                });
            }
            // console.log('changeData', changeData);
            this.events.emitStageResize(changeData, isRerenderSort);
        }
    }

    /** 自定义api *************************************************/

    /** 存储自定义数据 */
    setCustomData(key: string, value: unknown) {
        this.customData[key] = value;
    }

    /** 获取自定义数据 */
    getCustomData<T>(key: string) {
        const data = this.customData[key];
        return data ? (data as T) : null;
    }

    /** 获取图层行为 */
    getBehavior(layerOrLayerType: ILayerProps | LayerType) {
        return getBehavior(this.identity, layerOrLayerType);
    }
}
